import { APPROVAL_HEADER } from "./four-eyes";
import { ErrorCodes, ProblemError, networkProblemError, toProblemError } from "./problem";
import type { ProblemDocument } from "./problem";

// The /v1 API client (ADR 0015 §5). ONE place attaches the bearer and handles
// the cross-cutting controls — 401→refresh→retry, step-up re-auth, and the
// four-eyes approval cycle — so no surface re-implements governance per call.
// Everything is injected (auth handle, approval gateway, fetch) so the whole
// thing is unit-testable without a browser or a live IdP.

/** Token lifecycle the client depends on — implemented by the OIDC auth layer. */
export interface AuthHandle {
  /** The current in-memory access token (never read from localStorage). */
  getToken(): string | null;
  /** Silent renew; resolves to the fresh token, or null if renewal failed. */
  refresh(): Promise<string | null>;
  /** Interactive MFA step-up; resolves to a fresh (strong) token, or null if abandoned. */
  stepUp(): Promise<string | null>;
}

/** The four-eyes UX boundary: drive "request approval" → resolve to the approval
 *  id once a second authorized user has approved. Null = the user abandoned it. */
export interface ApprovalGateway {
  requestApproval(ctx: FourEyesContext): Promise<string | null>;
}

export interface FourEyesContext {
  method: string;
  path: string;
  problem: ProblemDocument;
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  /** Carried on a four-eyes retry — sent as the x-approval-id header. */
  approvalId?: string;
  /** Client-generated idempotency key (idempotency-key header) for safe mutation
   *  retries; reused across the client's own refresh/step-up retries of this call. */
  idempotencyKey?: string;
  /** Internal one-shot guards so a single request can't loop on one control. */
  _retriedAuth?: boolean;
  _retriedStepUp?: boolean;
  _retriedFourEyes?: boolean;
}

/** Options for a raw-bytes GET (see ApiClient.getBlob). */
export interface BlobOptions {
  /** Accept header for the stream (e.g. "application/pdf"). */
  accept?: string;
  signal?: AbortSignal;
  /** Internal one-shot guard so a single request can't loop on the 401 refresh. */
  _retriedAuth?: boolean;
}

export interface ApiClientDeps {
  baseUrl: string;
  auth: AuthHandle;
  approvals?: ApprovalGateway;
  fetchImpl?: typeof fetch;
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly auth: AuthHandle;
  private readonly approvals?: ApprovalGateway;
  private readonly fetchImpl: typeof fetch;

  constructor(deps: ApiClientDeps) {
    this.baseUrl = deps.baseUrl.replace(/\/$/, "");
    this.auth = deps.auth;
    this.approvals = deps.approvals;
    this.fetchImpl = deps.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  get<T>(path: string, opts: RequestOptions = {}): Promise<T> {
    return this.request<T>(path, { ...opts, method: "GET" });
  }
  post<T>(path: string, body?: unknown, opts: RequestOptions = {}): Promise<T> {
    return this.request<T>(path, { ...opts, method: "POST", body });
  }
  patch<T>(path: string, body?: unknown, opts: RequestOptions = {}): Promise<T> {
    return this.request<T>(path, { ...opts, method: "PATCH", body });
  }
  delete<T>(path: string, opts: RequestOptions = {}): Promise<T> {
    return this.request<T>(path, { ...opts, method: "DELETE" });
  }

  /** Fetch raw bytes (e.g. a PDF) with the bearer attached + one 401→refresh retry.
   *  Kept off request<T>() — that path negotiates application/json and JSON-parses the
   *  body, which a binary stream can't satisfy. A non-2xx (incl. an expired link) → a
   *  ProblemError, same as the JSON path. No four-eyes/step-up recovery (reads only). */
  async getBlob(path: string, opts: BlobOptions = {}): Promise<Blob> {
    const url = path.startsWith("http") ? path : `${this.baseUrl}${path}`;
    const headers: Record<string, string> = { accept: opts.accept ?? "application/octet-stream" };
    const token = this.auth.getToken();
    if (token) headers.authorization = `Bearer ${token}`;
    const res = await this.send(url, { method: "GET", headers, signal: opts.signal });
    if (res.status === 401 && !opts._retriedAuth) {
      const fresh = await this.auth.refresh();
      if (fresh) return this.getBlob(path, { ...opts, _retriedAuth: true });
    }
    if (!res.ok) throw await toProblemError(res);
    return res.blob();
  }

  /** Send multipart/form-data (a file upload) with the bearer attached + one
   *  401→refresh retry. Kept off request<T>() — that path forces a JSON body; here
   *  the FormData sets its OWN multipart content-type + boundary, so we never set it.
   *  A non-2xx → a ProblemError, same as the JSON path. */
  async postForm<T>(path: string, form: FormData, opts: BlobOptions = {}): Promise<T> {
    const url = path.startsWith("http") ? path : `${this.baseUrl}${path}`;
    const headers: Record<string, string> = { accept: "application/json" };
    const token = this.auth.getToken();
    if (token) headers.authorization = `Bearer ${token}`;
    const res = await this.send(url, {
      method: "POST",
      headers,
      body: form,
      signal: opts.signal,
    });
    if (res.status === 401 && !opts._retriedAuth) {
      const fresh = await this.auth.refresh();
      if (fresh) return this.postForm<T>(path, form, { ...opts, _retriedAuth: true });
    }
    if (!res.ok) throw await toProblemError(res);
    return this.parseOk<T>(res);
  }

  async request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
    const url = path.startsWith("http") ? path : `${this.baseUrl}${path}`;
    const headers: Record<string, string> = { accept: "application/json" };
    const token = this.auth.getToken();
    if (token) headers.authorization = `Bearer ${token}`;
    if (opts.approvalId) headers[APPROVAL_HEADER] = opts.approvalId;
    if (opts.idempotencyKey) headers["idempotency-key"] = opts.idempotencyKey;
    let payload: BodyInit | undefined;
    if (opts.body !== undefined) {
      headers["content-type"] = "application/json";
      payload = JSON.stringify(opts.body);
    }

    const res = await this.send(url, {
      method: opts.method ?? "GET",
      headers,
      body: payload,
      signal: opts.signal,
    });

    if (res.ok) return this.parseOk<T>(res);

    const problem = await toProblemError(res);
    return this.recover<T>(path, opts, problem);
  }

  /**
   * The ONE place a fetch is actually issued. Every transport path (request /
   * getBlob / postForm) goes through here so a fetch REJECTION becomes a
   * ProblemError exactly once, in exactly one shape.
   *
   * Before this, none of the three call sites had a try/catch: `toProblemError`
   * only ever ran on a non-2xx Response, so when the device was offline the raw
   * `TypeError: Failed to fetch` escaped and every `err instanceof ProblemError`
   * branch across console/admin/customer/web was unreachable. The apps had error
   * handling; it just could not fire in the one situation users hit most.
   *
   * An AbortError is NOT a network failure — a caller that aborted (unmount,
   * superseded query) is not offline and must keep seeing the abort, or React
   * Query will treat a cancellation as a hard error.
   */
  private async send(url: string, init: RequestInit): Promise<Response> {
    try {
      return await this.fetchImpl(url, init);
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") throw err;
      if (err instanceof Error && err.name === "AbortError") throw err;
      throw networkProblemError(err);
    }
  }

  private async parseOk<T>(res: Response): Promise<T> {
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }

  /** The governance interceptors. Each control fires at most once per request. */
  private async recover<T>(path: string, opts: RequestOptions, problem: ProblemError): Promise<T> {
    // 401 → silent refresh → retry once.
    if (problem.status === 401 && !opts._retriedAuth) {
      const fresh = await this.auth.refresh();
      if (fresh) return this.request<T>(path, { ...opts, _retriedAuth: true });
      throw problem;
    }

    // Step-up (403 / CSA-1007) → interactive MFA re-auth → retry once.
    if (problem.is(ErrorCodes.STEP_UP_REQUIRED) && !opts._retriedStepUp) {
      const fresh = await this.auth.stepUp();
      if (fresh) return this.request<T>(path, { ...opts, _retriedStepUp: true });
      throw problem;
    }

    // Four-eyes (403 / CSA-1008) → request a second-person approval → retry once
    // carrying x-approval-id. The console never self-approves; the API enforces ≠.
    if (problem.is(ErrorCodes.FOUR_EYES_REQUIRED) && !opts._retriedFourEyes && this.approvals) {
      const approvalId = await this.approvals.requestApproval({
        method: opts.method ?? "GET",
        path,
        problem: problem.problem,
      });
      if (approvalId) {
        return this.request<T>(path, { ...opts, approvalId, _retriedFourEyes: true });
      }
      throw problem;
    }

    throw problem;
  }
}
